import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Play, Clock, GraduationCap, Loader2, Mail, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MyLessons = () => {
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);

  useEffect(() => {
    // Check for saved email
    const savedEmail = localStorage.getItem('lesson_access_email');
    if (savedEmail) {
      setEmail(savedEmail);
      setEmailSubmitted(true);
      loadMyLessons(savedEmail);
    } else {
      setLoading(false);
    }
  }, []);

  const loadMyLessons = async (userEmail) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/lessons/my-lessons?user_email=${encodeURIComponent(userEmail)}`);
      setLessons(response.data.lessons || []);
    } catch (error) {
      console.error('Error loading lessons:', error);
      toast.error('Error loading your lessons');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (email) {
      localStorage.setItem('lesson_access_email', email);
      setEmailSubmitted(true);
      loadMyLessons(email);
    }
  };

  const changeEmail = () => {
    setEmailSubmitted(false);
    setLessons([]);
  };

  if (!emailSubmitted) {
    return (
      <div className="min-h-screen py-12 px-6">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border/50 rounded-2xl p-8 text-center"
          >
            <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
              <GraduationCap className="w-10 h-10 text-primary" />
            </div>
            
            <h1 className="text-2xl font-bold text-foreground mb-4">
              Access Your Purchased Lessons
            </h1>
            
            <p className="text-muted-foreground mb-6">
              Enter the email address you used to purchase your lessons.
            </p>

            <form onSubmit={handleEmailSubmit} className="max-w-md mx-auto">
              <div className="flex gap-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="flex-1 px-4 py-3 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:border-primary"
                  required
                />
                <button
                  type="submit"
                  className="px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl font-semibold transition-colors"
                >
                  View Lessons
                </button>
              </div>
            </form>

            <div className="mt-8 pt-6 border-t border-border/50">
              <p className="text-sm text-muted-foreground mb-4">
                Don't have any lessons yet?
              </p>
              <button
                onClick={() => navigate('/samba-lessons')}
                className="px-6 py-3 bg-gradient-to-r from-primary to-accent hover:opacity-90 text-primary-foreground rounded-full font-bold transition-all"
              >
                Browse Samba Lessons
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold font-display text-foreground mb-2">
                My Lessons
              </h1>
              <p className="text-muted-foreground flex items-center gap-2">
                <Mail className="w-4 h-4" />
                {email}
                <button
                  onClick={changeEmail}
                  className="text-primary hover:underline ml-2"
                >
                  Change
                </button>
              </p>
            </div>
            <button
              onClick={() => navigate('/samba-lessons')}
              className="px-5 py-2.5 bg-primary/20 hover:bg-primary/30 text-primary rounded-full font-semibold transition-colors"
            >
              Browse More Lessons
            </button>
          </div>
        </motion.div>

        {loading ? (
          <div className="text-center py-20">
            <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading your lessons...</p>
          </div>
        ) : lessons.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border/50 rounded-2xl p-12 text-center"
          >
            <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-bold text-foreground mb-2">
              No Lessons Found
            </h2>
            <p className="text-muted-foreground mb-6">
              You haven't purchased any lessons with this email yet.
            </p>
            <button
              onClick={() => navigate('/samba-lessons')}
              className="px-8 py-4 bg-gradient-to-r from-primary to-accent hover:opacity-90 text-primary-foreground rounded-full font-bold text-lg transition-all"
            >
              Browse Samba Lessons
            </button>
          </motion.div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {lessons.map((lesson, index) => (
              <motion.div
                key={lesson.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-card border border-border/50 rounded-2xl overflow-hidden hover:border-primary/50 transition-colors group cursor-pointer"
                onClick={() => navigate(`/lesson/${lesson.id}`)}
              >
                <div className="aspect-video relative overflow-hidden">
                  <img
                    src={lesson.thumbnail}
                    alt={lesson.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center">
                      <Play className="w-8 h-8 text-primary-foreground ml-1" />
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-foreground mb-2 line-clamp-2">
                    {lesson.title}
                  </h3>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    {lesson.duration}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyLessons;
